import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Flex from "../../components/Layout/Flex";
import TableLayout from "../../components/Layout/TableLayout";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import useDebounce from "../../hooks/useDebounce";
import Consultant, { Availability } from "../../models/Consultant";
import { useUpdateConsultant } from "../../networking/consultant.hooks";
import { useOrganisation } from "../../networking/organisation.hooks";

type AvailabilityDays = keyof Availability;

const EMPTY_AVAILABILITY: Availability = {
	monday: [],
	tuesday: [],
	wednesday: [],
	thursday: [],
	friday: [],
	saturday: [],
	sunday: [],
};

const ConsultantAvailabilityTab: React.FC<{ consultant: Consultant }> = ({ consultant }) => {
	const { t } = useTranslation();

	const { mutate: updateConsultant, isLoading } = useUpdateConsultant();
	const { data: organisation } = useOrganisation();
	const [value, setValue] = useState<Availability>(
		consultant.profile.availability || EMPTY_AVAILABILITY
	);

	const debouncedValue = useDebounce(value, 250);

	// Persist the user's (debounced) edits. Depends ONLY on debouncedValue so an
	// optimistic cache update of `consultant` can't feed back and re-trigger a
	// save. Compare by value (not reference) so an equivalent object flowing in
	// from the cache doesn't cause a spurious write.
	useEffect(() => {
		const current = consultant.profile.availability ?? EMPTY_AVAILABILITY;
		if (JSON.stringify(current) !== JSON.stringify(debouncedValue)) {
			updateConsultant({
				userId: consultant.id,
				silent: true,
				update: {
					profile: {
						availability: debouncedValue,
					},
				},
				optimisticUpdate: true,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedValue]);

	// Sync local state when the consultant changes from elsewhere, but skip when
	// the incoming data is equivalent so we don't clobber in-flight edits.
	useEffect(() => {
		const incoming = consultant.profile.availability;
		if (incoming && JSON.stringify(incoming) !== JSON.stringify(value)) {
			setValue(incoming);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [consultant]);

	if (!consultant) {
		return null;
	}

	return (
		<div>
			<TableLayout>
				<table>
					<thead>
						<tr>
							<th> {t("WEEKDAY")}</th>
							{organisation?.availabilityShifts?.map((timeSlots, index) => (
								<th key={index}>{`${timeSlots.start} - ${timeSlots.end}`}</th>
							))}
						</tr>
					</thead>
					<tbody>
						{(Object.keys(value) as Array<AvailabilityDays>).map((key) => {
							return (
								<tr key={key}>
									<td>{t(key.toUpperCase() as any)}</td>
									{organisation?.availabilityShifts?.map((shift, shiftIndex) => {
										return (
											<td key={shiftIndex}>
												<input
													onChange={() => {
														setValue((prev) => {
															const hasValue = prev[key].find((s) => s.start === shift.start);
															return {
																...prev,
																[key]: hasValue
																	? prev[key].filter((s) => s.start !== shift.start)
																	: [
																			...prev[key],
																			{
																				start: shift.start,
																				end: shift.end,
																			},
																	  ],
															};
														});
													}}
													defaultChecked={!!value[key].find((s) => s.start === shift.start)}
													type="checkbox"
												/>
											</td>
										);
									})}
								</tr>
							);
						})}
					</tbody>
				</table>
			</TableLayout>

			<Flex margin="1rem">{isLoading && <LoadingIndicator showText={false} />}</Flex>
		</div>
	);
};

export default ConsultantAvailabilityTab;
